/**
 * LT-16 Part 3a + 4a — Create Client Estimate dialog.
 *
 * The single commit path for a client-facing PDF. Pre-flight:
 *  1. Fetch tender overview to get compute_send_blockers().
 *  2. If any block-severity blocker: require explicit bypass reason.
 *  3. Output selection (defaults to full_estimate_pack — what Chris
 *     sends 95% of the time).
 *  4. Confirm dialog names exactly what will happen.
 *
 * The button lives in Adjudication, a working tab. It sits visually
 * apart from working controls and confirms twice — first the dialog,
 * then the confirm. Users cannot fire this by accident while
 * adjusting margin.
 */
import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, FileCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const OUTPUT_LABELS = {
  full_estimate_pack: "Full Estimate Pack (Summary + Detailed + A&O)",
  detailed_estimate:  "Detailed Estimate only",
  summary_cover:      "Summary Cover only",
  ao_v2:              "Assumptions & Omissions only",
};

export default function CreateClientEstimateDialog({ open, onOpenChange, tenderId, tenderRef, onIssued }) {
  const [blockers, setBlockers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selection, setSelection] = useState(["full_estimate_pack"]);
  const [bypassCodes, setBypassCodes] = useState([]);
  const [bypassReason, setBypassReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/tenders/${tenderId}/overview`)
      .then((r) => setBlockers(r.data?.pre_send_checks?.blockers || []))
      .catch(() => setBlockers([]))
      .finally(() => setLoading(false));
    setSelection(["full_estimate_pack"]);
    setBypassCodes([]);
    setBypassReason("");
  }, [open, tenderId]);

  const blocking = useMemo(() => blockers.filter((b) => b.severity === "block"), [blockers]);
  const warnings = useMemo(() => blockers.filter((b) => b.severity !== "block"), [blockers]);

  const canProceed = useMemo(() => {
    if (loading || saving) return false;
    if (selection.length === 0) return false;
    if (blocking.length === 0) return true;
    // Every blocker must be explicitly bypassed AND a reason given.
    const allBypassed = blocking.every((b) => bypassCodes.includes(b.code));
    return allBypassed && bypassReason.trim().length > 0;
  }, [loading, saving, selection, blocking, bypassCodes, bypassReason]);

  const toggleOutput = (k) => setSelection((s) =>
    s.includes(k) ? s.filter((x) => x !== k) : [...s, k],
  );
  const toggleBypass = (code) => setBypassCodes((c) =>
    c.includes(code) ? c.filter((x) => x !== code) : [...c, code],
  );

  const submit = async () => {
    if (!canProceed) return;
    setSaving(true);
    try {
      const r = await api.post(`/tenders/${tenderId}/adjudication/create-client-estimate`, {
        output_selection: selection,
        bypassed_blocker_codes: bypassCodes,
        bypass_reason: bypassReason.trim() || null,
      });
      const ref = r.data?.version?.version_ref;
      toast.success(`Version ${ref} prepared — remember to Mark as Sent once you've emailed the PDF.`);
      onIssued?.(r.data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || "Failed to create client estimate");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-start justify-center pt-16 px-4">
      <div
        className="w-full max-w-[640px] bg-surface border border-line rounded-lg shadow-card overflow-hidden"
        data-testid="create-client-estimate-dialog"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <h2 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>
              Create Client Estimate — {tenderRef}
            </h2>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
              This is the single commit for a client-facing document. Once you Mark it as Sent it becomes immutable.
            </p>
          </div>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="text-ink-3" style={{ fontSize: 12 }}>Loading pre-send checks…</div>
          ) : (
            <>
              {blocking.length > 0 && (
                <div className="mb-5 rounded-md border border-note-red/40 bg-note-red/5 p-4" data-testid="cce-blockers">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-note-red" />
                    <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                      {blocking.length} blocker{blocking.length === 1 ? "" : "s"} on this tender
                    </div>
                  </div>
                  <ul className="ml-6 space-y-1 mb-3 text-ink-2" style={{ fontSize: 12 }}>
                    {blocking.map((b) => (
                      <li key={b.code + b.line_id} className="flex items-start gap-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={bypassCodes.includes(b.code)}
                            onChange={() => toggleBypass(b.code)}
                            className="mt-1"
                            data-testid={`cce-bypass-${b.code}`}
                          />
                          <span>
                            <span className="font-mono text-ink-3 mr-1.5" style={{ fontSize: 10.5 }}>{b.code}</span>
                            {b.message}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <textarea
                    value={bypassReason}
                    onChange={(e) => setBypassReason(e.target.value)}
                    placeholder="Reason for issuing despite blockers (required — logged to audit)"
                    className="w-full min-h-[64px] rounded-md border border-line bg-surface px-2.5 py-1.5 text-ink outline-none focus:ring-2 focus:ring-accent/30"
                    style={{ fontSize: 12 }}
                    data-testid="cce-bypass-reason"
                  />
                </div>
              )}

              {warnings.length > 0 && blocking.length === 0 && (
                <div className="mb-5 rounded-md border border-note-amber/40 bg-note-amber/10 p-3 text-ink-2" style={{ fontSize: 12 }}>
                  <AlertTriangle size={12} className="inline mr-1 -mt-0.5 text-note-amber" />
                  {warnings.length} warning{warnings.length === 1 ? "" : "s"} on this tender. These don't block issuing but review before sending.
                </div>
              )}

              <div className="mb-4">
                <div className="text-ink" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>
                  Outputs to include
                </div>
                <div className="flex flex-col gap-1.5">
                  {Object.entries(OUTPUT_LABELS).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 cursor-pointer" data-testid={`cce-output-${k}`}>
                      <input
                        type="checkbox"
                        checked={selection.includes(k)}
                        onChange={() => toggleOutput(k)}
                      />
                      <span className="text-ink-2" style={{ fontSize: 12.5 }}>{label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-ink-3 mt-2" style={{ fontSize: 11 }}>
                  Selecting "Full Estimate Pack" ignores the individual selections — the pack is the whole document.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-surface-2/40 border-t border-line">
          <button
            onClick={() => onOpenChange(false)}
            className="h-9 px-3.5 rounded-md border border-line text-ink-2 hover:bg-surface-2"
            style={{ fontSize: 12.5 }}
            data-testid="cce-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canProceed}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-accent text-white shadow-soft hover:bg-accent-deep disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontSize: 12.5, fontWeight: 500 }}
            data-testid="cce-submit-btn"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <FileCheck size={13} />}
            Create version
          </button>
        </div>
      </div>
    </div>
  );
}
